import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DetectMethodChanges, DetectSetterChanges, isValidNumber, isVoid, logMessage} from "../utils";
import {QuoteGeneratorService} from "./quote-generator.service";
import {TradingInstrument} from "../trading-instruments/trading-instrument.class";
import {TradingInstrumentsService} from "../trading-instruments/trading-instruments-service.interface";
import {
    OptionExpirationDescriptor, QuoteGeneratorLoadedStreamDto,
    QuoteGeneratorProgressDto,
    QuoteGeneratorQuoteDto
} from "../shell-communication/shell-dto-protocol";
import {OptionsChainService} from "../option-chains.service";
import {DatasetSelectorComponent} from "./dataset-selector/dataset-selector.component";
import {MessageBusService} from "../message-bus.service";
import {makeOptionTicker, makeOptionTickerDisplayName, parseOptionTicker} from "../options-common/options.model";
import {Subject, Subscription} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {CustomScriptInputComponent} from "./custom-script-input/custom-script-input.component";
import {QuoteGeneratorMode, QuoteGeneratorQuoteSource, QuoteStream} from "./quote.stream";
import {
    ChangeHistoryDelta,
    HistoryDatasetDescriptor,
    SavedScriptDescriptor
} from "../shell-communication/shell-operations-protocol";
import {CustomScriptSaveComponent} from "./custom-script-save/custom-script-save.component";
import {AtmStrikeService} from "../common-services/atm-strike-service/atm-strike.service";
import {ApplicationSettingsService, AppSettingsUpdatedMessageTopic} from "../app-settings/application-settings.service";
import {ClickThrottler} from "./click-throttler.class";

@Component({
    selector: 'ets-quote-generator-component',
    templateUrl: 'quote-generator.component.html',
    styleUrls: ['quote-generator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuoteGeneratorComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _quoteGeneratorService: QuoteGeneratorService,
        private readonly _tiService: TradingInstrumentsService,
        private readonly _optionChainService: OptionsChainService,
        private readonly _messageBus: MessageBusService,
        private readonly _atmService: AtmStrikeService,
        private readonly _applicationSettingsService: ApplicationSettingsService
    ) {
    }

    private readonly _subscriptions: Subscription[] = [];

    private _isLoading = false;
    get isLoading(): boolean {
        return this._isLoading;
    }

    @DetectSetterChanges()
    set isLoading(value: boolean) {
        this._isLoading = value;
    }

    visible = false;

    pinnedStream: QuoteStream;

    streams: QuoteStream[] = [];

    addUnderlying: TradingInstrument;

    addExpirationList: OptionExpirationDescriptor[] = [];

    addExpiration: OptionExpirationDescriptor;

    addOptionType: 'Call' | 'Put';

    addStrike: number;

    addOptionContract = false;

    streamFrequencyChanged$ = new Subject<QuoteStream>();

    trendBiasChanged$ = new Subject<QuoteStream>();

    @ViewChild(CustomScriptSaveComponent)
    customScriptSaveDialog: CustomScriptSaveComponent;

    @ViewChild(DatasetSelectorComponent)
    datasetSelector: DatasetSelectorComponent;

    @ViewChild(CustomScriptInputComponent)
    customScriptInput: CustomScriptInputComponent;

    get isUnderlyingSelected(): boolean {
        return !isVoid(this.addUnderlying);
    }

    get isReducedView(): boolean {
        return !isVoid(this.pinnedStream);
    }

    ngOnInit() {

        this._quoteGeneratorService.init();

        this._subscriptions.forEach(x => x.unsubscribe());

        this._subscriptions.length = 0;

        this._subscriptions.push(
            this._quoteGeneratorService.atmChanged$.subscribe(() => this._changeDetector.detectChanges())
        );

        this._subscriptions.push(
            this._messageBus.of<any>('Qg.ScriptSelected')
                .subscribe(msg => this.onSavedScriptSelected(msg.payload))
        );

        this._subscriptions.push(
            this._messageBus.of<any>('Qg.EditScript')
                .subscribe(msg => this.onEditScriptClicked(msg.payload))
        );


        this._subscriptions.push(
            this._messageBus.of<any>('Qg.DeleteScript')
                .subscribe(msg => this.deleteScript(msg.payload))
        );


        this._subscriptions.push(
            this._messageBus.of<QuoteGeneratorQuoteDto>('QuoteGeneratorQuoteDto')
                .subscribe(msg => this.onQuoteGeneratorQuote(msg.payload))
        );


        this._subscriptions.push(
            this._messageBus.of<QuoteGeneratorProgressDto>('QuoteGeneratorProgressDto')
                .subscribe(msg => this.onQuoteGeneratorProgress(msg.payload))
        );

        this._subscriptions.push(
            this._messageBus.of<QuoteGeneratorLoadedStreamDto>('QuoteGeneratorLoadedStreamDto')
                .subscribe(msg => this.onQuoteGeneratorStreamLoaded(msg.payload))
        );


        this._subscriptions.push(
            this.streamFrequencyChanged$
                .pipe(
                    debounceTime(1000)
                ).subscribe(msg => this.setStreamFrequency(msg))
        );


        this._subscriptions.push(
            this.trendBiasChanged$
                .pipe(
                    debounceTime(1000)
                ).subscribe(msg => this.setTrendBias(msg))
        );

        this._subscriptions.push(
            this._messageBus.of<any>('Qg.RefreshLastQuoteRequest')
                .subscribe((msg) => this.onRefreshLastQuoteRequest(msg.payload))
        );

        this._subscriptions.push(
            this._messageBus.of<any>('Qg.AtmDistanceRequest')
                .subscribe(msg => this.onAtmDistanceRequested(msg.payload))
        );
    }

    private _throttlers: Record<string, ClickThrottler> = {};
    private _historyUpDownThrottlers: Record<string, ClickThrottler> = {};

    ngOnDestroy() {
        this._quoteGeneratorService.dispose();
        this._subscriptions.forEach(x => x.unsubscribe());
    }

    private setupClickThrottler(stream: QuoteStream, direction: string, isDelta = false) {

        const cb = isDelta
            ? (stream, direction, count) => this.fireDeltaChangeCommand(stream, count)
            : (stream, direction, count) => this.fireNextQuote(stream, direction, count);

        const clickThrottler = new ClickThrottler(
            stream,
            direction,
            () => (this._applicationSettingsService.adjustmentPricingGrid.delayedUpdates || 0) * 1000,
            cb
        );

        return clickThrottler;
    }

    private async fireNextQuote(stream: QuoteStream, direction: string, count: number) {

        console.log(`firing next quote. stream: ${stream.ticker}, direction: ${direction}, count: ${count}`);

        this.isLoading = true;
        this._messageBus.publish({topic: 'Qg.StreamRecalculating', payload: {ticker: stream.ticker}});

        const throttler = this._throttlers[stream.ticker];
        if (throttler) {
            throttler.destroy();
            this._throttlers[stream.ticker] = null;
        }

        try {
            await this._quoteGeneratorService
                .nextQuote(stream.ticker, direction as any, count, stream.quoteStep);
        } catch {
        } finally {
            this.isLoading = false;
        }

    }

    @DetectMethodChanges()
    async show() {

        if (this.isLoading) {
            return;
        }

        try {
            this.isLoading = true;
            this.streams = await this._quoteGeneratorService.getRegisteredStreams();
            const spxStream = this.streams.find(x => x.ticker === 'SPX');
            this.pinnedStream = spxStream;
            this.visible = true;
        } finally {
            this.isLoading = false;
        }
    }

    @DetectMethodChanges()
    hide() {
        this.visible = false;
    }

    private _suppressQuoteSourceChange = false;

    @DetectMethodChanges()
    setMode(stream: QuoteStream, mode: QuoteGeneratorMode) {
        this.isLoading = true;

        setTimeout(async () => {
            try {

                let quoteSource: QuoteGeneratorQuoteSource = stream.quoteSource;

                if (stream.quoteSource === 'Live') {
                    quoteSource = mode === 'History' ? 'Historical' : 'Theoretical';
                }

                if (mode === 'Manual') {
                    quoteSource = 'Live';
                }

                this._suppressQuoteSourceChange = true;
                stream.quoteSource = quoteSource;
                stream.mode = mode;

                await this._quoteGeneratorService.setModeAndSource(stream.ticker, mode, quoteSource);

                if (stream.mode !== 'History') {
                    await this.resetToMarketPrice(stream);

                    stream.lastTimestamp = null;

                    stream.jumpToDate = stream.jumpStep = null;

                    stream.dataset = null;

                    this._messageBus.publish({
                        topic: 'Qg.JumpToDateChangedMessage',
                        payload: {
                            ticker: stream.ticker,
                            jumpToDate: null,
                        }
                    });
                } else {

                    await this._quoteGeneratorService.requestHistoricalDatasetRange(stream);
                    stream.jumpStep = 1;
                    let ep = stream.dataset.endPoint;
                    if (!isVoid(ep)) {
                        const date = ep.split('T')[0];
                        ep = `${date}T09:31:00`;
                    }
                    stream.jumpToDate = ep;
                }

            } finally {
                this.isLoading = false;
                setTimeout(() => { this._suppressQuoteSourceChange = false; }, 0);
            }
        });
    }

    @DetectMethodChanges()
    toggleStreamRunning(stream: QuoteStream) {
        this.isLoading = true;
        setTimeout(async () => {
            try {
                await this._quoteGeneratorService.toggleStreamPlaying(stream);
                stream.toggleRunning();
            } finally {
                this.isLoading = false;
            }
        });
    }

    @DetectMethodChanges()
    onAddUnderlying($event: TradingInstrument) {
        this.isLoading = true;

        setTimeout(async () => {
            try {
                this.addUnderlying = $event;
                const chain = await this._optionChainService.getChain($event.ticker);
                this.addExpirationList = chain.expirations;
            } finally {
                this.isLoading = false;
            }
        });
    }

    @DetectMethodChanges()
    onAddExpirationChanged() {
    }

    @DetectMethodChanges()
    toggleExpandStream(stream?: QuoteStream) {
        this.pinnedStream = stream;
        if (isVoid(this.pinnedStream)) {
            this.isVerticalView = false;
        }
    }

    @DetectMethodChanges()
    onClose() {
        this.visible = false;
        this.pinnedStream = null;
    }

    @DetectMethodChanges()
    showDatasetSelector(stream: QuoteStream) {
        this.datasetSelector.show(stream, 'History');
    }

    @DetectMethodChanges()
    showSavedScriptSelector(stream: QuoteStream) {
        this.datasetSelector.show(stream, 'Script');
    }

    @DetectMethodChanges({isAsync: true})
    async showCustomScriptInput(stream: QuoteStream) {
        try {
            const script = await this.customScriptInput.show(stream);
            if (isVoid(script)) {
                return;
            }
            this.isLoading = true;
            await this._quoteGeneratorService.setCustomScript(stream, script);
        } catch {
            //
        } finally {
            this.isLoading = false;
        }
    }

    private editSavedScript(script: SavedScriptDescriptor) {
        this.customScriptInput.showEditor(script);
    }

    @DetectMethodChanges()
    onSavedScriptSelected(msg: { stream: QuoteStream, script: any }) {
        this.isLoading = true;
        setTimeout(async () => {
            try {
                msg.stream.savedScript = msg.script;
                msg.stream.customScript = null;
                await this._quoteGeneratorService.setSavedScript(msg.stream, msg.script.name);
            } finally {
                this.isLoading = false;
            }
        });
    }

    @DetectMethodChanges()
    async addStream() {

        this.isLoading = true;

        setTimeout(async () => {
            try {
                const ul = this.addUnderlying;

                const isOption = !isVoid(this.addExpiration)
                    || !isVoid(this.addOptionType) || !isVoid(this.addStrike);

                if (!isOption) {
                    await this._quoteGeneratorService.addStream(ul);
                    return;
                }

                const ticker = makeOptionTicker(this.addExpiration, this.addOptionType
                    , this.addStrike);

                const optionTicker = parseOptionTicker(ticker);

                const displayName = makeOptionTickerDisplayName(optionTicker);

                const ti = {ticker, displayName} as any;

                await this._quoteGeneratorService.addStream(ti);
            } finally {
                this.isLoading = false;
            }
        });

    }

    @DetectMethodChanges()
    onAddOptionContractChanged() {
        if (this.addOptionContract) {
            return;
        }

        this.addExpiration = null;
        this.addOptionType = null;
        this.addStrike = null;
    }

    @DetectMethodChanges()
    removeStream(stream: QuoteStream) {
        this._quoteGeneratorService.removeStream(stream);
    }

    @DetectMethodChanges({isAsync: true})
    async onLeftArrowClicked(stream: QuoteStream) {

        const delay = this._applicationSettingsService.adjustmentPricingGrid.delayedUpdates;

        if (!isValidNumber(delay, true) || !stream.isFastClickMode) {
            await this.fireNextQuote(stream, 'Backward', 1);
            return;
        }


        let throttler = this._throttlers[stream.ticker];

        if (isVoid(throttler) || throttler.getDirection() !== 'Backward') {
            if (!isVoid(throttler)) {
                throttler.destroy();
            }
            throttler = this.setupClickThrottler(stream, 'Backward');
            this._throttlers[stream.ticker] = throttler;
        }

        throttler.click();

        if (stream.isHistoryMode) {
            stream.updateTimestamp(-1);
        } else if (stream.isManualMode) {
            if (stream.quoteSource === 'Live') {
                stream.updateLastPrice(-1);
            }
        }
    }

    onLastClicked(stream: QuoteStream, silently?: boolean) {

        if (!silently) {
            this.isLoading = true;
        }

        setTimeout(async () => {
            try {
                await this._quoteGeneratorService.nextQuote(stream.ticker, 'Last', 1, stream.quoteStep);
            } finally {
                this.isLoading = false;
            }
        });
    }

    @DetectMethodChanges()
    async onRightArrowClicked(stream: QuoteStream) {

        const delay = this._applicationSettingsService.adjustmentPricingGrid.delayedUpdates;

        if (!isValidNumber(delay, true) || !stream.isFastClickMode) {
            await this.fireNextQuote(stream, 'Forward', 1);
            return;
        }

        let throttler = this._throttlers[stream.ticker];

        if (isVoid(throttler) || throttler.getDirection() !== 'Forward') {
            if (!isVoid(throttler)) {
                throttler.destroy();
            }
            throttler = this.setupClickThrottler(stream, 'Forward');
            this._throttlers[stream.ticker] = throttler;
        }

        throttler.click();

        if (stream.isHistoryMode) {
            stream.updateTimestamp(1);
        } else if (stream.isManualMode) {
            if (stream.quoteSource === 'Live') {
                stream.updateLastPrice(1);
            }
        }
    }

    @DetectMethodChanges({isAsync: true})
    async onHistoryUpArrowClicked(stream: QuoteStream) {
        const delay = this._applicationSettingsService.adjustmentPricingGrid.delayedUpdates;

        if (!isValidNumber(delay, true) || !stream.isFastClickMode) {
            stream.updateDelta(1);
            await this.fireDeltaChangeCommand(stream, 1);
            return;
        }

        let throttler = this._historyUpDownThrottlers[stream.ticker];

        if (isVoid(throttler) || throttler.getDirection() !== 'Backward') {
            if (!isVoid(throttler)) {
                throttler.destroy();
            }
            throttler = this.setupClickThrottler(stream, 'Backward', true);
            this._historyUpDownThrottlers[stream.ticker] = throttler;
        }

        throttler.click();

        if (!stream.isHistoryMode) {
            return;
        }

        stream.updateDelta(1);
    }

    @DetectMethodChanges({isAsync: true})
    async onHistoryDownArrowClicked(stream: QuoteStream) {
        const delay = this._applicationSettingsService.adjustmentPricingGrid.delayedUpdates;

        if (!isValidNumber(delay, true) || !stream.isFastClickMode) {
            stream.updateDelta(-1);
            await this.fireDeltaChangeCommand(stream, 1);
            return;
        }

        let throttler = this._historyUpDownThrottlers[stream.ticker];

        if (isVoid(throttler) || throttler.getDirection() !== 'Backward') {
            if (!isVoid(throttler)) {
                throttler.destroy();
            }
            throttler = this.setupClickThrottler(stream, 'Backward', true);
            this._historyUpDownThrottlers[stream.ticker] = throttler;
        }

        throttler.click();

        if (!stream.isHistoryMode) {
            return;
        }

        stream.updateDelta(-1);
    }

    private async fireDeltaChangeCommand(stream: QuoteStream, ticks: number) {

        console.log(`firing delta change. stream: ${stream.ticker}`);

        this.isLoading = true;
        this._messageBus.publish({topic: 'Qg.StreamRecalculating', payload: {ticker: stream.ticker}});

        const throttler = this._historyUpDownThrottlers[stream.ticker];
        if (throttler) {
            throttler.destroy();
            this._historyUpDownThrottlers[stream.ticker] = null;
        }

        try {
            await this._quoteGeneratorService.changeHistoryDelta(stream.ticker, stream.historyDelta);
        } catch {
        } finally {
            this.isLoading = false;
        }
    }

    @DetectMethodChanges()
    changeStreamFrequency(stream: QuoteStream) {
        this.streamFrequencyChanged$.next(stream);
    }

    @DetectMethodChanges()
    private onQuoteGeneratorQuote(payload: QuoteGeneratorQuoteDto) {
        try {
            this._quoteGeneratorService.onQuoteGeneratorQuote(payload);
        } catch {
        }
    }

    private async setStreamFrequency(stream: QuoteStream) {
        await this._quoteGeneratorService.setStreamFrequency(stream);
    }

    @DetectMethodChanges()
    private onQuoteGeneratorProgress(payload: QuoteGeneratorProgressDto) {
        this._quoteGeneratorService.onQuoteGeneratorProgress(payload);
    }

    @DetectMethodChanges()
    private onQuoteGeneratorStreamLoaded(payload: QuoteGeneratorLoadedStreamDto) {
        this._quoteGeneratorService.onQuoteGeneratorStreamLoaded(payload);
    }

    @DetectMethodChanges()
    async onMenuItemClicked(stream: QuoteStream, $event: { itemData: any }) {
        const command = $event.itemData.text || '';

        if (command.indexOf('Reset') > -1) {
            await this._quoteGeneratorService.resetStreamPriceToMarket(stream.ticker);
        }

    }

    // getNonTradingDays(stream: QuoteStream): Date[] {
    //     return stream?.dataset?.nonTradingDates.map(x => new Date(x));
    // }

    getMinDate(stream: QuoteStream) {
        return stream?.dataset?.startPoint;
    }

    getMaxDate(stream: QuoteStream) {
        return stream?.dataset?.endPoint;
    }

    getMaxDateForJump(stream: QuoteStream) {
        return stream?.dataset?.endPoint;
    }

    onTrendBiasChanged(stream: QuoteStream) {
        this.trendBiasChanged$.next(stream);
    }

    private async setTrendBias(msg: QuoteStream) {
        await this._quoteGeneratorService.setTrendBias(msg);
    }

    @DetectMethodChanges()
    saveCustomScript(stream: QuoteStream) {
        this.customScriptSaveDialog.show(stream);
    }

    canChangeMode(stream: QuoteStream) {
        return !stream.isRunning;
    }

    @DetectMethodChanges()
    setQuoteStep(stream: QuoteStream, number: number) {
        stream.quoteStep = number;
    }

    @DetectMethodChanges()
    toggleStreamMicroView(stream: QuoteStream) {
        stream.microView = !stream.microView;
    }

    getWidth() {
        if (this.isVerticalView) {
            if (this.isMicroView()) {
                // History-mode micro renders a 2-axis cross pad (price up/down +
                // time left/right) — needs more horizontal room than the single-row
                // micro used by Manual/Random/Script modes.
                if (this.pinnedStream?.isHistoryMode) {
                    return 190;
                }
                return 120;
            }

            if (this.pinnedStream) {
                if (this.pinnedStream.isHistoryMode) {
                    return 259;
                }
            }
            return 235;
        }


        let width = 800;

        if (this.isReducedView) {

            if (this.isMicroView()) {
                width = this.pinnedStream?.isHistoryMode ? 190 : 120;
            } else {
                const someShortened = this.streams.some(x => this.isManualView(x));

                if (someShortened) {
                    width = 485;
                } else {
                    const someHistory = this.streams.some(x => this.isHistoryView(x));
                    if (someHistory) {
                        width = 1000;
                    }
                }
            }
        }

        return width;
    }

    isMicroView(): boolean {
        return this.streams.some(x => x.microView);
    }

    isVerticalView = true;

    getHeight() {

        if (this.isVerticalView) {

            if (this.isMicroView()) {
                // History-mode micro is 3 rows tall (price-up / center / price-down)
                // for the cross-pad layout; non-History stays single-row.
                if (this.pinnedStream?.isHistoryMode) {
                    return 112;
                }
                return 70;
            }

            if (this.pinnedStream) {
                if (this.pinnedStream.isManualMode) {
                    return 345;
                } else if (this.pinnedStream.isHistoryMode) {
                    return 606;
                } else if (this.pinnedStream.isScriptMode) {
                    return 455;
                } else if (this.pinnedStream.isRandomMode) {
                    return 440;
                }
            }
            return 345;
        } else {
            let height = 465;
            if (this.isReducedView) {
                height = 165;
                if (this.isMicroView()) {
                    height = this.pinnedStream?.isHistoryMode ? 112 : 70;
                } else {
                    const someManual = this.streams.some(x => this.isManualView(x));

                    if (someManual) {
                        height = 195;
                    } else {
                        const someHistory = this.streams.some(x => this.isHistoryView(x));
                        if (someHistory) {
                            height = 205;
                        }
                    }
                }
            }
            return height;
        }
    }

    @DetectMethodChanges()
    getOutOfMicroView() {
        this.streams
            .filter(x => x.microView)
            .forEach(x => x.microView = false);
    }

    isResizable() {
        // return !this.isReducedView && !this.isMicroView();
        return true;
    }

    private onEditScriptClicked(payload: any) {
        this.customScriptInput.showEditor(payload.script);
    }

    private async deleteScript(payload: SavedScriptDescriptor) {
        await this._quoteGeneratorService.deleteScript(payload);
    }

    getAtm(stream: QuoteStream) {
        const currentAtm = this._quoteGeneratorService.getCurrentAtm(stream.ticker);
        return currentAtm;
    }

    getMenuItems(stream: QuoteStream) {
        return [
            {text: 'Reset To Market'},
            {text: (stream.isShowingAtm ? 'Hide' : 'Show') + ' ATM'}
        ]
    }

    @DetectMethodChanges()
    onMenuShowing(stream: QuoteStream) {
        stream.menuItems.forEach(item => {
            if (item.text.indexOf('ATM') < 0) {
                return;
            }
            item.text = stream.isShowingAtm ? 'Hide ATM Distance' : 'Show ATM Distance'
        });
    }

    getAtmDistance(stream: QuoteStream): string {
        try {
            // In History mode, anchor the title-bar differential against the historical
            // start point (i.e., stream.historyDelta) so it matches the value rendered
            // inside the expanded view's history-up-down section. The default
            // getAtmDistanceValue() formula reads against the LIVE proxy, which produces
            // a different (and confusing) number when the cursor sits on a past day.
            const diff = stream.mode === 'History'
                ? (stream.historyDelta || 0)
                : this.getAtmDistanceValue(stream);

            const sign = Math.sign(diff) > 0 ? '+$' : (Math.sign(diff) < 0 ? '-$' : '$');

            return `${sign}${Math.abs(diff)}`;
        } catch {
            return undefined;
        }
    }

    private getAtmDistanceValue(stream: QuoteStream) {
        const streamAtm = this._atmService
            .calculateCurrentAtm(stream.ticker, stream.lastPrice);

        const currentAtm = this._quoteGeneratorService
            .getCurrentAtm(stream.ticker);

        const diff = (streamAtm - currentAtm) || 0;

        return diff;
    }

    @DetectMethodChanges()
    moveMarketDown(stream: QuoteStream) {
        if (!isValidNumber(stream.atmDistance)) {
            stream.atmDistance = 0 - stream.quoteStep;
        } else {
            stream.atmDistance -= stream.quoteStep;
        }
    }

    @DetectMethodChanges()
    moveMarketUp(stream: QuoteStream) {
        if (!isValidNumber(stream.atmDistance)) {
            stream.atmDistance = 0 + stream.quoteStep;
        } else {
            stream.atmDistance += stream.quoteStep;
        }
    }

    async setQuoteSource(stream: QuoteStream, value: QuoteGeneratorQuoteSource) {
        if (this._suppressQuoteSourceChange) {
            return;
        }
        await this._quoteGeneratorService.setModeAndSource(stream.ticker, stream.mode, value);
    }

    canChangeQuoteSource(stream: QuoteStream) {
        return !stream.isRunning;
    }

    isManualView(stream: QuoteStream) {
        if (isVoid(this.pinnedStream)) {
            return false;
        }

        if (isVoid(stream)) {
            return false;
        }

        if (this.pinnedStream.ticker !== stream.ticker) {
            return false;
        }

        return stream.isManualMode;
    }

    isHistoryView(stream: QuoteStream) {
        if (isVoid(this.pinnedStream)) {
            return false;
        }

        if (isVoid(stream)) {
            return false;
        }

        if (this.pinnedStream.ticker !== stream.ticker) {
            return false;
        }

        return stream.isHistoryMode;
    }

    @DetectMethodChanges({isAsync: true})
    async resetToMarketPrice(stream: QuoteStream) {
        this.isLoading = true;
        try {
            await this._quoteGeneratorService.resetStreamPriceToMarket(stream.ticker);
        } finally {
            this.isLoading = false;
        }
    }


    @DetectMethodChanges()
    decrementStepSize(stream: QuoteStream) {
        let step = stream.quoteStep -= 1;

        if (step <= 0) {
            step = 1;
        }

        this.setQuoteStep(stream, step);
    }

    incrementStepSize(stream: QuoteStream) {
        let step = stream.quoteStep += 1;

        if (step <= 0) {
            step = 1;
        }

        this.setQuoteStep(stream, step);
    }

    async onJumpToDateChanged(stream: QuoteStream) {
        await this._quoteGeneratorService.setJumpToDate(stream);
    }

    async onJumpStepChanged(stream: QuoteStream) {
        await this._quoteGeneratorService.setJumpStep(stream);
    }

    @DetectMethodChanges()
    async changeJumpStep(stream: QuoteStream, step: number) {
        stream.jumpStep = step;
    }

    @DetectMethodChanges()
    toggleVerticalView() {
        this.isVerticalView = !this.isVerticalView;
    }

    private onRefreshLastQuoteRequest(payload: { ticker: string }) {

        const quoteStream = this.streams.find(x => x.ticker === payload.ticker);

        if (isVoid(quoteStream?.ticker)) {
            return;
        }

        this.onLastClicked(quoteStream, true);
    }

    onHistoryAtmClicked(stream: QuoteStream) {
        stream.historyDelta = 0;
        this.fireDeltaChangeCommand(stream, 1);
    }

    onAtmDistanceRequested(payload: {underlying: string, distance?: number}) {
        const quoteStream = this.streams.find(s => s.ticker === payload.underlying);

        if (isVoid(quoteStream)) {
            return;
        }

        try {
            const distance = this.getAtmDistanceValue(quoteStream);
            payload.distance = distance;
        } catch (e) {
            //
        }
    }
}